import React from "react";
import { Table } from "../../components/Table";
import { CircleIcon } from "../../icons/CircleIcon";
import { Badge } from "../../components/Circle";
import { ActionText, Box, Flex } from "../../components/Flex";
import { Label, Paragraph, Title } from "../../components/Text";
import { ShcGauge } from "./ShcGauge";
import { filter, sumBy, toLower } from "lodash";
import { Checkbox } from "../../components/Checkbox";
import { formatDate } from "../../utils";
import { Page404 } from "../../components/Page404";


const ShcFile = ({ data=[], file={}, loading, showPassed, onShowPassed, onShowRule, notFound }) => {
    const severities = {
        "h": { title: "High", color: "#E86565" },
        "m": { title: "Medium", color: "#EE994A" },
        "l": { title: "Low", color: "#55C36D" },
        "u": { title: "Unknown", color: "#9CA5B4" }
    };

    const fields = [
        { label: "upload id", attr: "uploadRef" },
        { label: "node name", attr: "nodeName" },
        { label: "upload date", attr: "uploadDatetime" },
        { label: "file name", attr: "fileName" },
    ];

    const columns = [
        {
            title: <CircleIcon size={16} />,
            dataIndex: '_severity',
            width: 50,
            render: (text, record, index) => <Badge color={severities[record.severity.toLowerCase()] ? severities[record.severity.toLowerCase()].color : null } />,
            sorter: (a, b) => { if (a && a.hasOwnProperty("severity")) return Object.keys(severities).indexOf(a.severity.toLowerCase()) - Object.keys(severities).indexOf(b.severity.toLowerCase())}
        },
        {
            title: "Rule ID",
            dataIndex: 'ruleId',
            width: 90,
            render: (text, record, index) => <ActionText onClick={() => onShowRule(text)}>{text}</ActionText>,
            sorter: (a, b) => { if (a && a.hasOwnProperty("ruleId") && a.ruleId) return a.ruleId.localeCompare(b.ruleId) }
        },
        {
            title: "Title",
            dataIndex: 'title',
            render: (text, record) => <Paragraph nowrap title={text}>{text}</Paragraph>,
            sorter: (a, b) => { if (a && a.hasOwnProperty("title") && a.title) return a.title.localeCompare(b.title) }
        },
        {
            title: "Severity",
            dataIndex: 'severity',
            width: 150,
            render: (text, record) => severities[text.toLowerCase()] ? severities[text.toLowerCase()].title : null,
            sorter: (a, b) => { if (a && a.hasOwnProperty("severity") && a.severity) return a.severity.localeCompare(b.severity) }
        },
        {
            title: "Category",
            dataIndex: 'packagesCategory',
            width: 180,
            render: (text, record) => <Paragraph nowrap title={text}>{text}</Paragraph>,
            sorter: (a, b) => { if (a && a.hasOwnProperty("packagesCategory") && a.packagesCategory) return a.packagesCategory.localeCompare(b.packagesCategory) }
        },
        {
            title: "Domain",
            dataIndex: 'domain',
            width: 180,
            render: (text, record) => <Paragraph nowrap title={text}>{text}</Paragraph>,
            sorter: (a, b) => { if (a && a.hasOwnProperty("domain") && a.domain) return a.domain.localeCompare(b.domain) }
        },
        {
            title: "Fired",
            dataIndex: 'totalFired',
            width: 78,
            sorter: (a, b) => { if (a && a.hasOwnProperty("totalFired") && a.totalFired) return a.totalFired - b.totalFired }
        },
        {
            title: "Failed",
            dataIndex: 'totalFailed',
            width: 78,
            sorter: (a, b) => { if (a && a.hasOwnProperty("totalFailed") && a.totalFailed) return a.totalFailed - b.totalFailed }
        },
        {
            title: "Passed",
            dataIndex: 'totalPassed',
            width: 78,
            sorter: (a, b) => { if (a && a.hasOwnProperty("totalPassed") && a.totalPassed) return a.totalPassed - b.totalPassed }
        }
    ];

    const getFailedTestsBySeverity = (severity) => {
        return sumBy(filter(data, d => toLower(d.severity) === severity), o => Number(o.totalFailed));
    };

    if (notFound) {
        return <Page404 />
    };

    return(
        <Box
            flatten
            height="calc(100% - 52px)"
            gap={{ bottom: 12 }}
        >
            <Title h6 isLoading={loading}>{formatDate(file.evidenceTimestamp, "DD-MM-YYYY HH:mm:ss")}</Title>
            <Flex flex={1} justify="space-between" align="center">
                {fields.map(field => <Flex key={field.attr} direction="column">
                    <Label bold>{field.label}</Label>
                    <Paragraph isLoading={loading}>{file[field.attr]}</Paragraph>
                </Flex>)}
            </Flex>
            <ShcGauge type="total" total={sumBy(data, "totalFired")} values={{ failed: sumBy(data, "totalFailed"), passed: sumBy(data, "totalPassed"), fired: sumBy(data, "totalFired") }} loading={loading} />
            <ShcGauge type="severity" total={sumBy(data, "totalFailed")} loading={loading} values={{
                    high: getFailedTestsBySeverity("h"),
                    medium: getFailedTestsBySeverity("m"),
                    low: getFailedTestsBySeverity("l"),
                    unk: file.totalFailed - getFailedTestsBySeverity("h") - getFailedTestsBySeverity("m") - getFailedTestsBySeverity("l"),
                }}
            />
            <Flex flex={1} justify="space-between" align="center">
                <Label bold>Tests table</Label>
                <Checkbox checked={showPassed} onChange={(e) => onShowPassed(e.target.checked)}><Label bold lineHeight="16px">Including tests passed</Label></Checkbox>
            </Flex>
            <Table columns={columns} dataSource={Array.isArray(data) ? showPassed ? data : filter(data, d => d.totalFailed > 0) : []} loading={loading} emptyText={{ text: `No tests were ${showPassed ? "fired" : "failed"} for selected dates` }} />
        </Box>
    )
}

export { ShcFile }